import { z } from 'zod';
import { attestationId, caseId, certificateId, checkId, counterpartyId, crmLinkId, documentId, evidenceId, inspectorId, jobId, ledgerEntryId, orgId, professionalRequestId, recommendationId, recordId, requirementId, rfpEventId, sourceId, sourcingLinkId, userId, } from '../ids.js';
import { attestationMethod, attestationOutcome, bodyDesignation, caseStatus, caseTrigger, certificateScheme, certificateStatus, confidence, connectionState, criticality, crmSystem, documentStatus, documentType, flagType, grcSystem, homeRegion, humanResponse, identifierType, inspectorKind, inspectorStanding, jurisdiction, monitoringState, professionalRequestStatus, professionalType, recommendationContext, recommendationKind, recordState, requirementOrigin, requirementStatus, rfpEventType, severity, sourceAuth, sourceCapability, sourceProtocol, sourceStatus, sourcingPlatform, syncState, tierSetBy, tpmTier, } from '../enums.js';
/** Garnet PRD §5.5–§5.6, §10, §13, §28–§33, §36. */
export const Certificate = z.object({
    id: certificateId,
    org_id: orgId,
    scheme: certificateScheme,
    number: z.string(),
    body: z.string(),
    body_designation: bodyDesignation,
    scope: z.object({
        device_groups: z.array(z.string()).default([]),
        classes: z.array(z.string()).default([]),
        basic_udi_dis: z.array(z.string()).default([]),
    }),
    valid_from: z.string().datetime().nullable(),
    valid_to: z.string().datetime().nullable(),
    status: certificateStatus,
});
export const Inspector = z.object({
    id: inspectorId,
    kind: inspectorKind,
    /** Sensitive data class: qualifications, accreditations, expiry. */
    credentials: z.array(z.object({ name: z.string(), issuer: z.string(), expires_at: z.string().datetime().nullable() })),
    coverage: z.object({
        jurisdictions: z.array(jurisdiction),
        asset_classes: z.array(z.string()),
        methods: z.array(attestationMethod),
    }),
    standing: inspectorStanding,
    rating: z.object({
        sla_hit_rate: z.number().min(0).max(1).nullable(),
        dispute_rate: z.number().min(0).max(1).nullable(),
    }),
});
export const Attestation = z.object({
    id: attestationId,
    org_id: orgId,
    check_id: checkId,
    inspector_id: inspectorId,
    method: attestationMethod,
    /** Structured per the method template (PRD §10.3); validated against it on submission. */
    findings: z.unknown(),
    outcome: attestationOutcome,
    media_ids: z.array(evidenceId),
    deadline: z.string().datetime(),
    signed_at: z.string().datetime().nullable(),
    signature: z.string().nullable(),
});
/** Method templates: required captures per method (PRD §10.3). Submission validates against this. */
export const METHOD_REQUIRED_CAPTURES = {
    physical_inspection: [
        'label_photos',
        'security_feature_photos',
        'tamper_seal_state',
        'calibration_record',
        'condition_grade',
        'udi_carrier_scan',
    ],
    as6171_test: ['test_plan_id', 'external_visual', 'xray', 'xrf', 'electrical_test_result', 'as6171_verdict'],
    manufacturer_callback: [
        'serial_range_query',
        'produced_confirmation',
        'sold_to_confirmation',
        'stolen_diverted_status',
        'responder_identity',
    ],
    document_forensics: ['document_images', 'issuer_check', 'date_signature_checks', 'anomaly_notes'],
};
export const Case = z.object({
    id: caseId,
    org_id: orgId,
    record_id: recordId,
    trigger: caseTrigger,
    flag_type: flagType.nullable(),
    severity,
    assignee: userId.nullable(),
    status: caseStatus,
    resolution: z
        .object({
        decision: z.string(),
        rationale: z.string().min(1),
        evidence_added: z.array(evidenceId).default([]),
        /** Required to move a record off `blocked` (PRD §13.2). */
        compliance_signoff_by: userId.nullable(),
        resolved_by: userId,
        resolved_at: z.string().datetime(),
    })
        .nullable(),
    sla_due: z.string().datetime(),
    opened_at: z.string().datetime(),
});
/** A configured connector (PRD §7.2). */
export const Source = z.object({
    id: sourceId,
    name: z.string(),
    capabilities: z.array(sourceCapability),
    protocol: sourceProtocol,
    auth: sourceAuth,
    freshness_window_seconds: z.number().int().positive(),
    rate_limit: z.object({ rps: z.number().positive().nullable(), per_day: z.number().int().nullable() }),
    residency: homeRegion.nullable(),
    status: sourceStatus,
    connection: connectionState,
    mapping_version: z.string(),
});
/** Append-only unit observation (PRD §9.1). */
export const LedgerEntry = z.object({
    id: ledgerEntryId,
    org_id: orgId,
    identifier_type: identifierType,
    identifier_value: z.string(),
    udi_pi_or_serial: z.string(),
    consignment_id: z.string(),
    counterparty_id: counterpartyId,
    record_id: recordId.nullable(),
    observed_at: z.string().datetime(),
    hash: z.string().regex(/^[0-9a-f]{64}$/),
    prev_hash: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
    seq: z.number().int().nonnegative(),
});
/** Renewal-tracked credential (PRD §28.1). */
export const GarnetDocument = z.object({
    id: documentId,
    org_id: orgId,
    owner_kind: z.enum(['counterparty', 'entity']),
    owner_id: z.string(),
    type: documentType,
    scheme: z.string().nullable(),
    number: z.string().nullable(),
    issuer: z.string().nullable(),
    jurisdiction: jurisdiction.nullable(),
    valid_from: z.string().datetime().nullable(),
    valid_to: z.string().datetime().nullable(),
    status: documentStatus,
    renewal_of: documentId.nullable(),
    evidence_id: evidenceId.nullable(),
    linked_checks: z.array(checkId),
    /** OCR seam (PRD §27): per-field confidence + page anchors; null on the manual path. */
    extracted_fields: z
        .array(z.object({
        field: z.string(),
        value: z.string(),
        confidence: z.number().min(0).max(1),
        page_anchor: z.string().nullable(),
        confirmed_by: userId.nullable(),
    }))
        .nullable(),
});
export const ProfessionalRequest = z.object({
    id: professionalRequestId,
    org_id: orgId,
    type: professionalType,
    trigger_check: checkId.nullable(),
    subject_kind: z.enum(['document', 'entity']),
    subject_id: z.string(),
    jurisdiction,
    country: z.string(),
    language_pair: z.object({ from: z.string(), to: z.string() }).nullable(),
    professional_id: z.string().nullable(),
    deadline: z.string().datetime(),
    status: professionalRequestStatus,
    /** Country-scaled cost with triggered line items folded into one total (PRD §29.5–29.6). */
    cost: z.object({
        currency: z.string().length(3),
        line_items: z.array(z.object({ act: professionalType, base_minor: z.number().int(), scaled_minor: z.number().int(), triggered_by: professionalType.nullable() })),
        total_minor: z.number().int(),
    }),
    deliverable_document_id: documentId.nullable(),
    tracking_log: z.array(z.object({ at: z.string().datetime(), actor: z.string(), action: z.string(), note: z.string().nullable() })),
});
export const TpmClassification = z.object({
    org_id: orgId,
    counterparty_id: counterpartyId,
    tier: tpmTier,
    criticality,
    risk_inputs: z.object({
        asset_risk_class: z.string().nullable(),
        jurisdiction_risk: z.enum(['low', 'medium', 'high']).nullable(),
        spend_band: z.enum(['low', 'medium', 'high']).nullable(),
        regulatory_exposure: z.enum(['low', 'medium', 'high']).nullable(),
        prior_flags: z.number().int().nonnegative(),
    }),
    set_by: tierSetBy,
    rationale: z.string().nullable(),
    review_due: z.string().datetime(),
    monitoring: z.object({
        last_verified_at: z.string().datetime().nullable(),
        next_due: z.string().datetime(),
        state: monitoringState,
    }),
    history: z.array(z.object({
        tier: tpmTier,
        set_by: tierSetBy,
        actor: z.string(),
        rationale: z.string().nullable(),
        at: z.string().datetime(),
    })),
});
export const Requirement = z.object({
    id: requirementId,
    org_id: orgId,
    diligence_id: jobId,
    origin: requirementOrigin,
    ask: z.string().min(1),
    severity,
    status: requirementStatus,
    linked_check: checkId.nullable(),
    received_document_id: documentId.nullable(),
    thread: z.array(z.object({ at: z.string().datetime(), author: z.string(), body: z.string() })),
});
/**
 * Advisor output (PRD §33.6). The ONLY thing the Advisor can persist (Invariant I3) —
 * recommendations propose human actions; they never carry state changes.
 */
export const Recommendation = z.object({
    id: recommendationId,
    org_id: orgId,
    context: recommendationContext,
    subject_kind: z.enum(['counterparty', 'record', 'check', 'case', 'event']),
    subject_id: z.string(),
    kind: recommendationKind,
    body: z.unknown(),
    rationale: z.string().min(1),
    /** Mandatory grounding (PRD §33.1): an ungrounded recommendation is suppressed upstream. */
    grounding: z
        .array(z.object({
        kind: z.enum(['evidence', 'source', 'rule', 'policy', 'document', 'ledger']),
        ref: z.string(),
    }))
        .min(1),
    confidence,
    proposed_action: z.object({ label: z.string(), action: z.string(), params: z.unknown() }),
    human_response: humanResponse,
    responded_by: userId.nullable(),
    model_version: z.string(),
    prompt_version: z.string(),
    created_at: z.string().datetime(),
    responded_at: z.string().datetime().nullable(),
});
export const CrmLink = z.object({
    id: crmLinkId,
    org_id: orgId,
    system: crmSystem,
    external_id: z.string(),
    garnet_object_kind: z.string(),
    garnet_object_id: z.string(),
    last_synced_at: z.string().datetime().nullable(),
    sync_state: syncState,
});
export const RfpEvent = z.object({
    id: rfpEventId,
    org_id: orgId,
    platform: sourcingPlatform,
    external_event_id: z.string(),
    type: rfpEventType,
    required_tpm_tier: tpmTier.nullable(),
    required_record_state: recordState.nullable(),
    suppliers: z.array(counterpartyId),
    status: z.enum(['importing', 'open', 'awarded', 'closed']),
    synced_at: z.string().datetime().nullable(),
});
export const SourcingLink = z.object({
    id: sourcingLinkId,
    org_id: orgId,
    platform: sourcingPlatform,
    event_id: rfpEventId,
    supplier_external_id: z.string(),
    counterparty_id: counterpartyId,
    bid_ref: z.string().nullable(),
    eligibility: z.enum(['eligible', 'ineligible', 'pending']),
});
export const GrcLink = z.object({
    org_id: orgId,
    system: grcSystem,
    external_id: z.string(),
    counterparty_id: counterpartyId,
    last_pushed_at: z.string().datetime().nullable(),
    sync_state: syncState,
});
export const entityRef = z.object({ kind: z.string(), id: z.string() });
//# sourceMappingURL=operations.js.map